import { ref } from "vue";
import store from "@/store/index";
import { myContainer } from "@/inversify.config";
import { TYPES } from "@/types";
import type {
  IOdinAdminService,
  OdinAccessSession,
} from "@/interfaces/IOdinAdminService";

// Module-level (singleton) state so the global support banner and the Odin
// customers view share the same active-session list — granting/revoking in one
// place updates the banner reactively everywhere. ANSKNI-54.
const sessions = ref<OdinAccessSession[]>([]);
const loaded = ref(false);

function service(): IOdinAdminService {
  return myContainer.get<IOdinAdminService>(TYPES.IOdinAdminService);
}

export function useOdinAccess() {
  const refresh = async (): Promise<void> => {
    try {
      sessions.value = await service().getMyOdinAccess();
    } catch {
      sessions.value = [];
    }
    loaded.value = true;
  };

  const grant = async (
    containerId: number,
    reason: string,
    durationHours: number
  ): Promise<void> => {
    await service().grantOdinAccess(containerId, reason, durationHours);
    await refresh();
  };

  // Returns true if the active container was the revoked one and we switched away
  // from it (so callers can navigate out of the now-inaccessible container).
  const revoke = async (containerId: number): Promise<boolean> => {
    await service().revokeOdinAccess(containerId);
    await refresh();

    // If we just revoked access to the container we're currently working in, we
    // must leave it — otherwise the UI stays pointed at a container we can no
    // longer load. Switch to any container we still have access to.
    if (store.getters.container?.id !== containerId) return false;
    await store.dispatch("loadContainers");
    const remaining = (store.getters.containers ?? []).filter(
      (c: { id: number }) => c.id !== containerId
    );
    if (remaining.length > 0) {
      await store.dispatch("loadContainer", remaining[0].id);
    }
    return true;
  };

  const hasAccess = (containerId: number): boolean =>
    sessions.value.some((s) => s.containerId === containerId);

  return { sessions, loaded, refresh, grant, revoke, hasAccess };
}
